import React, { useEffect } from "react";
import { ArrowLeft, Sparkles, Target, Rocket, ShieldCheck, Smartphone, Store, FileText, CheckCircle2, AlertTriangle } from "lucide-react";

const LOGO = "/paras-logo.png";
const TITLE = "About Paras CashBack | Merchant & Customer Rewards Platform";
const DESC = "Learn about Paras CashBack, a merchant-supported customer rewards platform designed to make everyday shopping more engaging through cashback and promotional reward opportunities.";

const DIFF = [
  ["01", "Simple experience", "Shop, receive a token, scratch and discover your result.", Smartphone],
  ["02", "Merchant-supported promotions", "Rewards are part of promotional campaigns supported through participating merchants.", Store],
  ["03", "Digital experience", "Customers can view tokens, rewards and wallet activity through the Paras CashBack platform.", Sparkles],
  ["04", "Transparent information", "Applicable eligibility, campaign conditions and reward terms are communicated to customers.", FileText],
  ["05", "Responsible platform", "We focus on secure transactions, proper verification, fraud prevention and accurate reward records.", ShieldCheck],
];

export default function AboutPage() {
  useEffect(() => {
    const prev = document.title;
    document.title = TITLE;
    const m = document.querySelector('meta[name="description"]');
    const prevDesc = m ? m.getAttribute("content") : null;
    if (m) m.setAttribute("content", DESC);
    return () => { document.title = prev; if (m && prevDesc) m.setAttribute("content", prevDesc); };
  }, []);

  return (
    <div className="min-h-screen bg-[#070B14] text-slate-100" data-testid="about-page">
      {/* top bar */}
      <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-5">
        <a href="/" className="flex items-center gap-2" data-testid="about-brand">
          <img src={LOGO} alt="Paras CashBack" className="h-9 w-9 rounded-lg object-contain" />
          <span className="font-heading text-base font-extrabold">Paras Cashback™</span>
        </a>
        <a href="/" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-emerald-400" data-testid="about-back">
          <ArrowLeft className="h-4 w-4" /> Home
        </a>
      </div>

      {/* hero */}
      <header className="paras-grid-bg relative overflow-hidden border-y border-white/10 bg-gradient-to-br from-emerald-500/10 via-[#0b111d] to-cyan-500/10">
        <div className="mx-auto max-w-3xl px-5 py-16 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 font-mono text-[11px] uppercase tracking-widest text-emerald-400">
            <Sparkles className="h-3.5 w-3.5" /> About us
          </span>
          <h1 className="mt-5 font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">About Paras CashBack</h1>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-slate-300">
            Paras CashBack is a merchant-supported rewards platform designed to make everyday shopping more engaging, rewarding and memorable. Our platform connects participating merchants with customers through a simple token-based promotional experience.
          </p>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-slate-400">
            Customers can receive Paras CashBack Tokens from participating merchants on qualifying purchases. They can then open and scratch their token to discover whether they have received an eligible cashback reward or a lucky draw ticket.
          </p>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-16">
        {/* vision + mission */}
        <div className="grid gap-6 md:grid-cols-2">
          <div className="ledger-card rounded-3xl border border-white/10 p-8">
            <div className="grid h-12 w-12 place-items-center rounded-xl bg-emerald-500/15 text-emerald-400"><Target className="h-6 w-6" /></div>
            <h2 className="mt-5 font-heading text-2xl font-bold">Our Vision</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">To create a simple and enjoyable rewards ecosystem where customers can discover value from everyday shopping and merchants can build stronger customer engagement.</p>
          </div>
          <div className="ledger-card rounded-3xl border border-white/10 p-8">
            <div className="grid h-12 w-12 place-items-center rounded-xl bg-cyan-500/15 text-cyan-400"><Rocket className="h-6 w-6" /></div>
            <h2 className="mt-5 font-heading text-2xl font-bold">Our Mission</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">To provide merchants with a digital promotional rewards platform that is simple to use, transparent in its customer experience and built with security and responsible operations in mind.</p>
          </div>
        </div>

        {/* what makes different */}
        <section className="mt-16">
          <div className="text-center">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-emerald-400">Why Paras CashBack</p>
            <h2 className="mt-3 font-heading text-3xl font-bold">What makes us different</h2>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {DIFF.map(([n, t, d, I]) => (
              <div key={n} className="hover-lift group relative rounded-2xl border border-white/10 bg-gradient-to-b from-[#111827] to-[#0b111d] p-6">
                <span className="absolute right-5 top-4 font-heading text-3xl font-extrabold text-white/5 transition group-hover:text-emerald-500/20">{n}</span>
                <div className="grid h-11 w-11 place-items-center rounded-xl bg-emerald-500/15 text-emerald-400"><I className="h-5 w-5" /></div>
                <h3 className="mt-4 font-heading text-base font-semibold">{t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* commitment */}
        <section className="mt-16 rounded-3xl border border-emerald-500/20 bg-emerald-500/[0.06] p-8 text-center">
          <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-400" />
          <h2 className="mt-4 font-heading text-2xl font-bold">Our Commitment</h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-slate-300">We aim to continuously improve the Paras CashBack experience for both customers and participating merchants while maintaining clear policies and responsible platform operations.</p>
        </section>

        {/* disclaimer */}
        <div className="mt-8 flex gap-3 rounded-2xl border border-amber-500/20 bg-amber-500/[0.05] p-5">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-400" />
          <p className="text-xs leading-relaxed text-slate-400">
            <span className="font-semibold text-amber-300">Disclaimer:</span> Paras CashBack rewards are promotional benefits subject to applicable campaign terms, eligibility and availability. Nothing on this website should be interpreted as an investment opportunity, guaranteed income or financial return.
          </p>
        </div>

        <div className="mt-12 text-center">
          <a href="/login" className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-8 py-3.5 font-semibold text-slate-950 hover:bg-emerald-400" data-testid="about-cta">Get Started</a>
        </div>
      </main>

      <footer className="border-t border-white/10 py-8 text-center">
        <p className="text-xs text-slate-600">© {new Date().getFullYear()} Paras Cashback™ · <a href="/privacy" className="hover:text-emerald-400">Privacy</a> · <a href="/terms" className="hover:text-emerald-400">Terms</a> · <a href="/contact" className="hover:text-emerald-400">Contact</a></p>
      </footer>
    </div>
  );
}
